'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

interface AssessmentSection {
  id: string;
  title: string;
  description: string;
  annexRef: string;
  questions: Question[];
}

interface Question {
  id: string;
  text: string;
  controlRef?: string;
  weight: number;
}

interface Answers {
  [key: string]: boolean | null;
}

const sections: AssessmentSection[] = [
  {
    id: 'organizational',
    title: 'Organizational Controls',
    description: 'Annex A.5 - Policies, roles, and responsibilities',
    annexRef: 'A.5',
    questions: [
      { id: 'o1', text: 'Do you have documented information security policies?', controlRef: 'A.5.1', weight: 10 },
      { id: 'o2', text: 'Are security responsibilities clearly defined?', controlRef: 'A.5.2', weight: 9 },
      { id: 'o3', text: 'Is there segregation of duties for critical functions?', controlRef: 'A.5.3', weight: 8 },
      { id: 'o4', text: 'Do you have management commitment to information security?', controlRef: 'A.5.4', weight: 9 },
      { id: 'o5', text: 'Do you maintain contact with relevant authorities?', controlRef: 'A.5.5', weight: 6 },
      { id: 'o6', text: 'Is there a threat intelligence program?', controlRef: 'A.5.7', weight: 7 },
      { id: 'o7', text: 'Is security integrated into project management?', controlRef: 'A.5.8', weight: 8 },
    ],
  },
  {
    id: 'people',
    title: 'People Controls',
    description: 'Annex A.6 - Human resources security',
    annexRef: 'A.6',
    questions: [
      { id: 'p1', text: 'Are background checks performed before employment?', controlRef: 'A.6.1', weight: 8 },
      { id: 'p2', text: 'Do employment contracts include security responsibilities?', controlRef: 'A.6.2', weight: 8 },
      { id: 'p3', text: 'Is there security awareness training for all staff?', controlRef: 'A.6.3', weight: 9 },
      { id: 'p4', text: 'Is there a disciplinary process for security violations?', controlRef: 'A.6.4', weight: 7 },
      { id: 'p5', text: 'Are there defined responsibilities after employment ends?', controlRef: 'A.6.5', weight: 7 },
      { id: 'p6', text: 'Are confidentiality agreements in place?', controlRef: 'A.6.6', weight: 8 },
      { id: 'p7', text: 'Do you support remote working securely?', controlRef: 'A.6.7', weight: 8 },
    ],
  },
  {
    id: 'physical',
    title: 'Physical Controls',
    description: 'Annex A.7 - Physical and environmental security',
    annexRef: 'A.7',
    questions: [
      { id: 'ph1', text: 'Are physical security perimeters defined and controlled?', controlRef: 'A.7.1', weight: 8 },
      { id: 'ph2', text: 'Are entry controls in place for secure areas?', controlRef: 'A.7.2', weight: 9 },
      { id: 'ph3', text: 'Are offices and facilities secured appropriately?', controlRef: 'A.7.3', weight: 7 },
      { id: 'ph4', text: 'Is there physical security monitoring?', controlRef: 'A.7.4', weight: 7 },
      { id: 'ph5', text: 'Are there environmental threat protections?', controlRef: 'A.7.5', weight: 8 },
      { id: 'ph6', text: 'Is equipment sited and protected appropriately?', controlRef: 'A.7.8', weight: 7 },
      { id: 'ph7', text: 'Are assets securely disposed of or reused?', controlRef: 'A.7.14', weight: 8 },
    ],
  },
  {
    id: 'technological',
    title: 'Technological Controls',
    description: 'Annex A.8 - Technology and system security',
    annexRef: 'A.8',
    questions: [
      { id: 't1', text: 'Are user endpoints protected and managed?', controlRef: 'A.8.1', weight: 9 },
      { id: 't2', text: 'Are privileged access rights managed and controlled?', controlRef: 'A.8.2', weight: 10 },
      { id: 't3', text: 'Is access to information restricted appropriately?', controlRef: 'A.8.3', weight: 9 },
      { id: 't4', text: 'Is access to source code controlled?', controlRef: 'A.8.4', weight: 8 },
      { id: 't5', text: 'Is multi-factor authentication implemented?', controlRef: 'A.8.5', weight: 9 },
      { id: 't6', text: 'Is capacity management performed?', controlRef: 'A.8.6', weight: 7 },
      { id: 't7', text: 'Is there malware protection?', controlRef: 'A.8.7', weight: 9 },
      { id: 't8', text: 'Are technical vulnerabilities managed?', controlRef: 'A.8.8', weight: 9 },
      { id: 't9', text: 'Is configuration management implemented?', controlRef: 'A.8.9', weight: 8 },
      { id: 't10', text: 'Is data at rest and in transit encrypted?', controlRef: 'A.8.24', weight: 10 },
    ],
  },
  {
    id: 'operations',
    title: 'Operations Security',
    description: 'Annex A.8 continued - Operational procedures',
    annexRef: 'A.8',
    questions: [
      { id: 'op1', text: 'Are operating procedures documented?', controlRef: 'A.8.10', weight: 8 },
      { id: 'op2', text: 'Is change management controlled?', controlRef: 'A.8.32', weight: 9 },
      { id: 'op3', text: 'Are development, test, and production separated?', controlRef: 'A.8.31', weight: 8 },
      { id: 'op4', text: 'Is there backup and recovery capability?', controlRef: 'A.8.13', weight: 9 },
      { id: 'op5', text: 'Are logs generated and monitored?', controlRef: 'A.8.15', weight: 9 },
      { id: 'op6', text: 'Are clock sources synchronized?', controlRef: 'A.8.17', weight: 6 },
      { id: 'op7', text: 'Is software installation controlled?', controlRef: 'A.8.19', weight: 7 },
    ],
  },
  {
    id: 'supplier',
    title: 'Supplier Relationships',
    description: 'Annex A.5.19-5.23 - Third-party security',
    annexRef: 'A.5.19',
    questions: [
      { id: 's1', text: 'Is there a supplier security policy?', controlRef: 'A.5.19', weight: 9 },
      { id: 's2', text: 'Are security requirements in supplier agreements?', controlRef: 'A.5.20', weight: 9 },
      { id: 's3', text: 'Is ICT supply chain security managed?', controlRef: 'A.5.21', weight: 8 },
      { id: 's4', text: 'Are supplier services monitored and reviewed?', controlRef: 'A.5.22', weight: 8 },
      { id: 's5', text: 'Are changes to supplier services managed?', controlRef: 'A.5.23', weight: 7 },
    ],
  },
  {
    id: 'incident',
    title: 'Incident Management',
    description: 'Annex A.5.24-5.28 - Security incident handling',
    annexRef: 'A.5.24',
    questions: [
      { id: 'i1', text: 'Is there an incident management process?', controlRef: 'A.5.24', weight: 10 },
      { id: 'i2', text: 'Are security events reported and assessed?', controlRef: 'A.5.25', weight: 9 },
      { id: 'i3', text: 'Is there an incident response capability?', controlRef: 'A.5.26', weight: 9 },
      { id: 'i4', text: 'Are lessons learned from incidents?', controlRef: 'A.5.27', weight: 8 },
      { id: 'i5', text: 'Is evidence collected and preserved?', controlRef: 'A.5.28', weight: 7 },
    ],
  },
  {
    id: 'continuity',
    title: 'Business Continuity',
    description: 'Annex A.5.29-5.30 - Continuity and redundancy',
    annexRef: 'A.5.29',
    questions: [
      { id: 'c1', text: 'Is business continuity planning in place?', controlRef: 'A.5.29', weight: 9 },
      { id: 'c2', text: 'Is ICT readiness for business continuity ensured?', controlRef: 'A.5.30', weight: 9 },
      { id: 'c3', text: 'Are continuity plans tested regularly?', weight: 8 },
      { id: 'c4', text: 'Is there redundancy for critical systems?', weight: 8 },
    ],
  },
];

export default function ISO27001Page() {
  const [view, setView] = useState<'landing' | 'assessment' | 'results'>('landing');
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [saved, setSaved] = useState(false);

  const {
    enabled: commandCenterEnabled,
    saving,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'iso27001-gap-analysis',
    toolCategory: 'compliance',
  });

  const handleAnswer = (questionId: string, value: boolean) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const calculateResults = () => {
    let totalWeight = 0;
    let earnedWeight = 0;

    const sectionScores = sections.map((section) => {
      let sectionTotal = 0;
      let sectionEarned = 0;

      section.questions.forEach((q) => {
        sectionTotal += q.weight;
        totalWeight += q.weight;
        if (answers[q.id] === true) {
          sectionEarned += q.weight;
          earnedWeight += q.weight;
        }
      });

      return {
        id: section.id,
        title: section.title,
        annexRef: section.annexRef,
        score: sectionTotal > 0 ? Math.round((sectionEarned / sectionTotal) * 100) : 0,
        gaps: section.questions.filter((q) => answers[q.id] !== true),
      };
    });

    return {
      overallScore: totalWeight > 0 ? Math.round((earnedWeight / totalWeight) * 100) : 0,
      sectionScores,
    };
  };

  const results = view === 'results' ? calculateResults() : null;

  const handleSaveToCommandCenter = async () => {
    if (!results || !commandCenterEnabled) return;
    const scores: Record<string, number> = {};
    results.sectionScores.forEach(s => { scores[s.id] = s.score; });
    const gaps = results.sectionScores.flatMap(section =>
      section.gaps.map(gap => ({
        id: gap.id,
        title: gap.text,
        severity: section.score < 40 ? 'critical' as const : section.score < 60 ? 'high' as const : 'medium' as const,
        description: `Gap in ${section.title}`,
      }))
    );
    const assessment = await saveResult({
      name: `ISO 27001 Gap Analysis - ${new Date().toLocaleDateString()}`,
      score_overall: results.overallScore,
      scores,
      answers,
      gaps,
    });
    if (assessment) setSaved(true);
  };

  const exportReport = () => {
    if (!results) return;

    const lines = [
      '# ISO 27001:2022 Gap Analysis Report',
      '',
      `**Date:** ${new Date().toLocaleDateString()}`,
      `**Overall Readiness:** ${results.overallScore}%`,
      '',
      '---',
      '',
      '## Control Domain Scores',
      '',
      ...results.sectionScores.map((s) => `- **${s.title} (${s.annexRef}):** ${s.score}%`),
      '',
      '## Control Gaps',
      '',
    ];

    results.sectionScores.forEach((section) => {
      if (section.gaps.length > 0) {
        lines.push(`### ${section.title}`);
        lines.push('');
        section.gaps.forEach((gap) => {
          lines.push(`- [ ] ${gap.controlRef ? `[${gap.controlRef}] ` : ''}${gap.text}`);
        });
        lines.push('');
      }
    });

    lines.push('## Certification Path');
    lines.push('');
    lines.push('1. Complete gap remediation');
    lines.push('2. Implement ISMS documentation');
    lines.push('3. Conduct internal audit');
    lines.push('4. Management review');
    lines.push('5. Stage 1 audit (documentation review)');
    lines.push('6. Stage 2 audit (implementation verification)');
    lines.push('');
    lines.push('---');
    lines.push('*Generated by The Art of CTO - ISO 27001 Gap Analysis Tool*');

    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `iso27001-gap-analysis-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 40) return 'text-accent';
    return 'text-red-400';
  };

  return (
    <div className="min-h-screen bg-white dark:bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <nav className="mb-8 text-sm text-light-text-secondary dark:text-foreground-tertiary">
          <Link href="/tools" className="hover:text-accent transition-colors">Tools</Link>
          <span className="mx-2">/</span>
          <span className="text-light-text dark:text-foreground">ISO 27001 Gap Analysis</span>
        </nav>

        {view === 'landing' && (
          <>
            <div className="text-center mb-12">
              <div className="inline-block px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium mb-4">
                ISO/IEC 27001:2022
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-light-text dark:text-foreground mb-4">
                ISO 27001 Gap Analysis
              </h1>
              <p className="text-xl text-light-text-secondary dark:text-foreground-secondary max-w-3xl mx-auto">
                Assess your readiness for ISO 27001 certification across all Annex A control domains.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {[
                { name: 'Organizational', count: '37 controls' },
                { name: 'People', count: '8 controls' },
                { name: 'Physical', count: '14 controls' },
                { name: 'Technological', count: '34 controls' },
              ].map((domain) => (
                <div key={domain.name} className="bg-light-surface dark:bg-surface rounded-lg p-4 text-center">
                  <div className="font-medium text-light-text dark:text-foreground">{domain.name}</div>
                  <div className="text-sm text-light-text-secondary dark:text-foreground-tertiary">{domain.count}</div>
                </div>
              ))}
            </div>

            <div className="text-center mb-12">
              <button
                onClick={() => setView('assessment')}
                className="px-8 py-4 bg-accent text-accent-foreground rounded-lg text-lg font-bold hover:bg-accent-hover transition-colors"
              >
                Start Gap Analysis
              </button>
              <p className="text-sm text-light-text-secondary dark:text-foreground-tertiary mt-3">
                Based on ISO 27001:2022 Annex A controls
              </p>
            </div>

            <div className="bg-light-surface dark:bg-surface rounded-lg p-8">
              <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6">Why ISO 27001?</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ul className="text-sm text-light-text-secondary dark:text-foreground-secondary space-y-2">
                  <li>• Internationally recognized security standard</li>
                  <li>• Demonstrates security commitment to customers</li>
                  <li>• Risk-based approach to security</li>
                </ul>
                <ul className="text-sm text-light-text-secondary dark:text-foreground-secondary space-y-2">
                  <li>• Required by many enterprise clients</li>
                  <li>• Competitive advantage in tenders</li>
                  <li>• Continuous improvement framework</li>
                </ul>
              </div>
            </div>
          </>
        )}

        {view === 'assessment' && (
          <>
            <div className="mb-8">
              <div className="flex justify-between text-sm text-light-text-secondary dark:text-foreground-tertiary mb-2">
                <span>{sections[currentSection].title}</span>
                <span>Section {currentSection + 1} of {sections.length}</span>
              </div>
              <div className="h-2 bg-light-border dark:bg-surface rounded-full overflow-hidden">
                <div className="h-full bg-accent transition-all" style={{ width: `${((currentSection + 1) / sections.length) * 100}%` }} />
              </div>
            </div>

            <div className="bg-light-surface dark:bg-surface rounded-lg p-8 mb-8">
              <div className="flex items-center gap-3 mb-2">
                <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-sm font-mono">{sections[currentSection].annexRef}</span>
                <h2 className="text-2xl font-bold text-light-text dark:text-foreground">{sections[currentSection].title}</h2>
              </div>
              <p className="text-light-text-secondary dark:text-foreground-secondary mb-8">{sections[currentSection].description}</p>

              <div className="space-y-6">
                {sections[currentSection].questions.map((question) => (
                  <div key={question.id} className="border-b border-light-border dark:border-border-muted pb-6">
                    <div className="flex items-start gap-2 mb-3">
                      {question.controlRef && (
                        <span className="text-xs px-2 py-1 bg-light-border dark:bg-background text-light-text-secondary dark:text-foreground-tertiary rounded font-mono">{question.controlRef}</span>
                      )}
                      <p className="text-light-text dark:text-foreground">{question.text}</p>
                    </div>
                    <div className="flex gap-4">
                      <button onClick={() => handleAnswer(question.id, true)} className={`px-6 py-2 rounded-lg font-medium transition-colors ${answers[question.id] === true ? 'bg-green-500 text-white' : 'bg-light-border dark:bg-background text-light-text dark:text-foreground hover:bg-green-500/20'}`}>Yes</button>
                      <button onClick={() => handleAnswer(question.id, false)} className={`px-6 py-2 rounded-lg font-medium transition-colors ${answers[question.id] === false ? 'bg-red-500 text-white' : 'bg-light-border dark:bg-background text-light-text dark:text-foreground hover:bg-red-500/20'}`}>No</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between">
              <button onClick={() => currentSection > 0 ? setCurrentSection(currentSection - 1) : setView('landing')} className="px-6 py-3 border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground hover:border-accent transition-colors">{currentSection > 0 ? '← Previous' : '← Back'}</button>
              <button onClick={() => currentSection < sections.length - 1 ? setCurrentSection(currentSection + 1) : setView('results')} className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-medium hover:bg-accent-hover transition-colors">{currentSection < sections.length - 1 ? 'Next Section →' : 'View Results →'}</button>
            </div>
          </>
        )}

        {view === 'results' && results && (
          <>
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-light-text dark:text-foreground mb-4">Gap Analysis Results</h1>
              <div className={`text-6xl font-bold mb-2 ${getScoreColor(results.overallScore)}`}>{results.overallScore}%</div>
              <p className="text-light-text-secondary dark:text-foreground-secondary">{results.overallScore >= 80 ? 'Ready for certification audit' : results.overallScore >= 60 ? 'Significant progress - continue remediation' : 'Substantial work required before certification'}</p>
            </div>

            <div className="bg-light-surface dark:bg-surface rounded-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6">Control Domain Scores</h2>
              <div className="space-y-4">
                {results.sectionScores.map((section) => (
                  <div key={section.id}>
                    <div className="flex justify-between mb-2">
                      <span className="text-light-text dark:text-foreground"><span className="font-mono text-green-400 mr-2">{section.annexRef}</span>{section.title}</span>
                      <span className={getScoreColor(section.score)}>{section.score}%</span>
                    </div>
                    <div className="h-3 bg-light-border dark:bg-background rounded-full overflow-hidden">
                      <div className={`h-full transition-all ${section.score >= 80 ? 'bg-green-500' : section.score >= 60 ? 'bg-yellow-500' : section.score >= 40 ? 'bg-accent' : 'bg-red-500'}`} style={{ width: `${section.score}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-4 justify-center">
              <button onClick={exportReport} className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-medium hover:bg-accent-hover transition-colors">Export Report</button>
              {commandCenterEnabled && (
                <button
                  onClick={handleSaveToCommandCenter}
                  disabled={saving || saved}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : saved ? 'Saved to Command Center' : 'Save to Command Center'}
                </button>
              )}
              <button onClick={() => { setView('landing'); setCurrentSection(0); setAnswers({}); setSaved(false); }} className="px-6 py-3 border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground hover:border-accent transition-colors">Start Over</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
